import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { JdsButton } from "../../../../components/jds/JdsButton";
import "../../../../components/jds/jds.css";
import "../../consultas.css";
import schema from "./schema";

const ACCEPTED_EXTENSIONS = [".png", ".jpg", ".jpeg", ".pdf"];

const FileUploader = ({
  uploadUrl,
  setNeedRefetch,
  setIsCreateBlackListModalOpen
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [message, setMessage] = useState(null);

  const [products, setProducts] = useState([]);

  const [formData, setFormData] = useState({
    documentType: "CPF",
    document: "",
    queryTypes: [],
    observation: "",
    active: true
  });

  const getProducts = async () => {
    const response = await axios.get(
      process.env.REACT_APP_API_URL + "/black_list_document/products"
    );
    setProducts(response.data.data);
  };

  useEffect(() => {
    getProducts();
  }, []);

  const handleFileChange = event => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
      setMessage(null);
    }
  };

  const handleFileDrop = event => {
    event.preventDefault();
    setDragging(false);
    const dropped = event.dataTransfer.files && event.dataTransfer.files[0];
    if (!dropped) return;
    const name = dropped.name.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.some(ext => name.endsWith(ext))) {
      toast.error("Formato não suportado. Use PNG, JPG ou PDF.");
      return;
    }
    setFile(dropped);
    setMessage(null);
  };

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleQueryTypeChange = e => {
    const { value, checked } = e.target;
    const numericValue = parseInt(value);

    setFormData(prev => {
      if (checked) {
        return { ...prev, queryTypes: [...prev.queryTypes, numericValue] };
      } else {
        return {
          ...prev,
          queryTypes: prev.queryTypes.filter(id => id !== numericValue)
        };
      }
    });
  };

  const validateForm = async () => {
    try {
      await schema.validate(formData, { abortEarly: false });
      return true;
    } catch (err) {
      if (err.inner) {
        err.inner.forEach(e => toast.error(e.message));
      } else {
        toast.error(err.message);
      }
      return false;
    }
  };

  const handleUpload = async () => {
    const isValid = await validateForm();
    if (!isValid) return;
    const data = new FormData();
    data.append("documentType", formData.documentType);
    data.append("document", formData.document);
    formData.queryTypes.forEach(item => {
      data.append("queryType", item);
    });
    data.append("active", formData.active);

    if (formData.observation) {
      data.append("observation", formData.observation);
    }
    if (file) {
      data.append("attachment", file);
    }

    setUploading(true);
    setMessage(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/${uploadUrl}`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );

      setMessage("Upload realizado com sucesso!");
      setFile(null);
      setNeedRefetch(true);
      toast.success("Sucesso ao criar o documento");
      setIsCreateBlackListModalOpen(false);
    } catch (error) {
      setMessage(
        `Erro no upload: ${error.response?.data?.message || error.message}`
      );
    } finally {
      setUploading(false);
    }
  };

  if (!products || products.length <= 0) {
    return <p className="text-muted">Carregando...</p>;
  }

  return (
    <div>
      <div className="form-group">
        <label htmlFor="blacklist-document-type">
          Tipo de documento <span className="text-danger">*</span>
        </label>
        <select
          id="blacklist-document-type"
          className="form-control"
          name="documentType"
          value={formData.documentType}
          onChange={handleInputChange}
        >
          <option value="CPF">CPF</option>
          <option value="CNPJ">CNPJ</option>
          <option value="PLATE">Placa</option>
          <option value="PHONE">Telefone</option>
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="blacklist-document">
          Documento <span className="text-danger">*</span>
        </label>
        <input
          id="blacklist-document"
          type="text"
          className="form-control"
          name="document"
          value={formData.document}
          onChange={handleInputChange}
        />
      </div>

      <div className="form-group">
        <label id="blacklist-query-types-label">
          Tipos de consulta <span className="text-danger">*</span>
        </label>
        <div
          className="consultas-checks"
          role="group"
          aria-labelledby="blacklist-query-types-label"
        >
          {products.map(option => (
            <label key={option.id} className="consultas-check">
              <input
                type="checkbox"
                value={option.id}
                checked={formData.queryTypes.includes(option.id)}
                onChange={handleQueryTypeChange}
              />
              {option.name}
            </label>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="blacklist-observation">Observações</label>
        <textarea
          id="blacklist-observation"
          className="form-control"
          name="observation"
          rows={3}
          value={formData.observation}
          onChange={handleInputChange}
        />
      </div>

      <div className="form-group">
        <label className="consultas-check">
          <input
            type="checkbox"
            name="active"
            checked={formData.active}
            onChange={handleInputChange}
          />
          Documento ativo
        </label>
      </div>

      <div className="form-group">
        <label htmlFor="blacklist-attachment">Anexo</label>
        <label
          htmlFor="blacklist-attachment"
          className={`consultas-dropzone${dragging ? " is-dragging" : ""}`}
          onDragOver={event => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleFileDrop}
        >
          <input
            id="blacklist-attachment"
            className="consultas-dropzone__input"
            type="file"
            accept=".png,.jpg,.jpeg,.pdf"
            onChange={handleFileChange}
          />
          <span className="consultas-dropzone__body">
            <span>Clique para selecionar ou arraste o arquivo aqui</span>
            <span className="consultas-dropzone__hint">PNG, JPG ou PDF</span>
          </span>
        </label>
        {file && (
          <p className="consultas-dropzone__file">
            Arquivo selecionado: {file.name}
          </p>
        )}
      </div>

      <div className="consultas-form__actions">
        <JdsButton onClick={handleUpload} disabled={uploading}>
          {uploading ? "Enviando..." : "Enviar documento"}
        </JdsButton>
      </div>

      {message && (
        <p
          className={`consultas-feedback${
            message.includes("Erro") ? " consultas-feedback--error" : ""
          }`}
          role={message.includes("Erro") ? "alert" : "status"}
        >
          {message}
        </p>
      )}
    </div>
  );
};

export default FileUploader;
